// ============================================================
// DREAD - daring to attack an exposed target at all (v8.354.0).
//
//   dread=±N   attackers must pass a Willpower test at ±N to attack
//
// This is the Fear rule with teeth: Fear (X) on a sheet resolves to one
// of the four dread presets (Disturbing 0, Frightening −10, Horrifying
// −20, Terrifying −30), and anything can wear one by hand. The test
// runs through the resistance engine as vector `fear`, so Fearless is
// immune, Nerves of Steel re-rolls, Resistance (Fear) adds its bonus,
// and a Machine or a daemon feels nothing.
//
// ONCE PER ENCOUNTER (the ruling): an attacker tests the first time
// they dare a given target and the answer is remembered on their own
// state - `dreadTested`, keyed by the target - so the same horror is
// not re-rolled every swing. A table may rule per attack instead
// (`dreadPerAttack`), in which case nothing is remembered.
//
// FAILURE: the attack is lost - no roll, no ammo - and the attacker is
// shaken for the degrees of failure: `Pen:all=-10:<DoF>`.
//
// Pure. The attack hook asks through a callback (it does not know who
// is swinging); the composition root resolves here and writes the actor.
// ============================================================
import { activeExposures, axisTotal, exposureLabel, type ExposureBearer, type ExposureReader } from './exposures';
import { resistTest, describeResist, type ResistOutcome } from './resistances';
import type { Target } from '../types';

/** What the attacker remembers, keyed by the target's id: the answer, and what they were daring. */
export type DreadMemo = Record<string, { verdict: 'passed' | 'failed'; label: string }>;

export interface DreadCall {
  /** The worst modifier among the dread exposures worn, and its name. Null when nothing dreads. */
  modifier: number;
  label: string;
}

/** The dread the bearer imposes on this reader, if any. Several sum, like every axis. */
export function dreadOf(bearer: ExposureBearer, reader: ExposureReader): DreadCall | null {
  const active = activeExposures(bearer, reader).filter((ex) => typeof ex.axes.dread === 'number');
  if (!active.length) return null;
  return { modifier: axisTotal(active, 'dread'), label: active.map(exposureLabel).join(' + ') };
}

export interface DreadOutcome {
  /** Nothing to dare, or already answered: the attack simply proceeds. */
  needed: boolean;
  proceed: boolean;
  /** The test rolled, when one was. */
  test: ResistOutcome | null;
  /** The condition the attacker gains on a failure. */
  penalty: string | null;
  /** The memo to write back, when the table remembers. */
  memo: DreadMemo;
  note: string | null;
}

/** No test: the attack proceeds, with an optional note. */
const noTest = (memo: DreadMemo, note: string | null): DreadOutcome => ({
  needed: false,
  proceed: true,
  test: null,
  penalty: null,
  memo,
  note,
});

/** The note for an answer the attacker already gave to this same dread. */
function rememberedNote(verdict: 'passed' | 'failed', label: string): string {
  return verdict === 'passed'
    ? `Dread: already faced ${label} - no test.`
    : `Dread: already shaken by ${label} - no test.`;
}

/** A rolled test's outcome: steeled and proceeding, or the attack lost and the attacker shaken for the DoF. */
function facedOutcome(out: ResistOutcome, label: string, memo: DreadMemo): DreadOutcome {
  const described = describeResist(out, 'WP test');
  if (out.passed) {
    const note = `😨 Dread (${label}): ${described} - the attacker steels themselves.`;
    return { needed: true, proceed: true, test: out, penalty: null, memo, note };
  }
  const dof = Math.max(1, out.test?.dof ?? 1);
  const shaken = `shaken (−10 to everything for ${dof} ${dof === 1 ? 'round' : 'rounds'})`;
  const note = `😨 Dread (${label}): ${described} - the attack is LOST, and the attacker is ${shaken}.`;
  return { needed: true, proceed: false, test: out, penalty: `Pen:all=-10:${dof}`, memo, note };
}

/**
 * Decide whether the attacker dares.
 *
 * `memo` is the attacker's own record; `targetId` the key. With
 * `perAttack` the memo is neither read nor written. A remembered
 * answer for a DIFFERENT dread (the label changed - the thing got
 * worse) is re-asked.
 */
export function resolveDread(
  attacker: Target,
  bearer: ExposureBearer,
  reader: ExposureReader,
  targetId: string,
  memo: DreadMemo = {},
  opts: { perAttack?: boolean } = {},
): DreadOutcome {
  const call = dreadOf(bearer, reader);
  if (!call) return noTest(memo, null);
  const remembered = opts.perAttack ? undefined : memo[targetId];
  if (remembered && remembered.label === call.label)
    return noTest(memo, rememberedNote(remembered.verdict, call.label));
  const out = resistTest(attacker as unknown as Record<string, unknown>, 'fear', 'willpower', 0, call.modifier);
  const verdict: 'passed' | 'failed' = out.passed ? 'passed' : 'failed';
  const nextMemo = opts.perAttack ? memo : { ...memo, [targetId]: { verdict, label: call.label } };
  return facedOutcome(out, call.label, nextMemo);
}
